import * as Moment from 'moment'
import { extendMoment } from 'moment-range'
import { Unit } from '@interfaces/LearningPath'
import findIndex from 'lodash/findIndex'
import { FormValueProps, IAvailabilityBulkPayload } from '@components/Modal/AvailabilityBulkDeleteModal/interface'
import PortalInstructorAvailability from '@classes/InstructorAvailability'

const moment = extendMoment(Moment)

// more common helpers here
let queryString: any = null

if (typeof window !== 'undefined') {
  queryString = require('query-string')
}

export const getQueryParams = (field: string, url?: string) => {
  if (!queryString || !window) {
    return null
  }
  const removeHash = url?.split('#')?.[0]
  const cleanedUrl = removeHash?.split('?').pop()

  const currentQuery = queryString.parse(cleanedUrl || window.location.search) || {}
  return (currentQuery[field] || '').replace(' ', '+')
}

export const removeQueryParams = (fields: string[], url?: string) => {
  if (!queryString || !window) {
    return null
  }

  const parsed = queryString.parse(url || window.location.search)

  fields.forEach((currentField) => {
    delete parsed[currentField]
  })
  const { origin, pathname } = window.location
  const separator = Object.keys(parsed).length === 0 ? '' : '?'
  let finalUrl = `${origin}${pathname}${separator}${queryString.stringify(parsed)}`
  finalUrl = finalUrl.endsWith('?') ? finalUrl.slice(0, -1) : finalUrl
  return finalUrl
}

export const formQueryString = (obj) => queryString.stringify(obj)

export const createMarkup = (markup) => ({ __html: markup })

export const getFileExtension = (filename, defaultExt = 'others') =>
  (filename.split('.').pop() || defaultExt).toLowerCase()

export const isEmail = (email) => /\S+@\S+\.\S+/.test(email)

export const isGodMode = (isAdmin?: boolean) =>
  isAdmin && (getQueryParams('godMode') === '1' || localStorage.getItem('gm') === '1')

export const getUnitPrevIndex = (units: Unit[], unitid: number, depth: number = -1) => {
  const currentIndex = findIndex(units, { unitid })
  return currentIndex > 0 ? currentIndex - depth : currentIndex
}

export const convertMomentToDate = (date: moment.Moment = moment()) => new Date(date.clone().format('llll'))

export const urlStringify = (params: { [key: string]: any }) => {
  if (!queryString || !window) {
    return undefined
  }

  return queryString.stringify(params)
}

export const getFilteredBulkDeletePayload = (
  values: FormValueProps,
  Availabilities: PortalInstructorAvailability[]
): IAvailabilityBulkPayload[] => {
  return Availabilities.filter(({ CalendarStart, CalendarEnd, Type }) => {
    const EventStart = moment.tz(CalendarStart, values.Timezone)
    const EventEnd = moment.tz(CalendarEnd, values.Timezone)
    const eventRange = moment.range(EventStart, EventEnd)
    const selectedRange = moment.range(values.StartDate.clone().startOf('day'), values.EndDate.clone().endOf('day'))
    return (
      values.AvailabilityType.toLowerCase() === Type.toLowerCase() &&
      selectedRange.contains(eventRange) &&
      values.RepeatOn.includes(EventStart.weekday())
    )
  }).map((Availability) => Availability.getBulkDeletePayload(values.KeepLessons))
}

export const getIdAndTypeforProfile = (user: any) => {
  const cid2a = user?.attributes?.attributes?.['custom:customerId']
  const cid2aPayload = user?.signInUserSession?.idToken?.payload?.['custom:customerId']
  const cid2aFederated = user?.attributes?.attributes?.['custom:federatedExternalId']
  const cid2aFederatedPayload = user?.signInUserSession?.idToken?.payload?.['custom:federatedExternalId']
  const email = user?.attributes?.attributes?.email
  const emailPayload = user?.signInUserSession?.idToken?.payload?.email

  const cid2b = user?.attributes?.attributes?.['custom:customerId2b']
  const cid2bPayload = user?.signInUserSession?.idToken?.payload?.['custom:customerId2b']

  if (cid2a || cid2aPayload) {
    return {
      id: cid2a || cid2aPayload,
      type: 'StudentId',
    }
  }

  if (cid2aFederated || cid2aFederatedPayload) {
    return {
      id: cid2aFederated || cid2aFederatedPayload,
      type: 'ExternalIdentifier',
    }
  }
  // Added for supporting STAR/2B Student (customerId2b) as primary identifier
  if (cid2b || cid2bPayload) {
    return {
      id: cid2b || cid2bPayload,
      type: 'StudentId',
    }
  }

  if (email || emailPayload) {
    return {
      id: email || emailPayload,
      type: 'Email',
    }
  }

  console.error(`No valid identifier found for user profile: ${JSON.stringify(user)}! Control should never reach here!`)

  return {
    id: 0,
    type: 'StudentId',
  }
}

export const getIdAndTypeForProfForAdmin = (accessAs: any) => {
  const { aas, aai } = accessAs
  if (aai) {
    return {
      id: aai,
      type: 'Email',
    }
  } else if (aas) {
    return {
      id: aas,
      type: 'StudentId',
    }
  } else {
    return {
      id: 0,
      type: 'StudentId',
    }
  }
}

export const extractNumbeFromStr = (input: string | null | undefined): number | null => {
  if (!input) return null // Handle null or undefined input

  const numberMatch = input.match(/\d+/)
  return numberMatch ? parseInt(numberMatch[0], 10) : null
}

/**
 * Moves an object with a specific value in a given field to the first position in the array.
 * This function **modifies the original array**.
 *
 * @param {Array} array - The array of objects to modify.
 * @param {*} value - The value to match in the specified field.
 * @param {string} field - The key (field) to search for the value.
 *
 * @returns {Array} The modified array with the matching object moved to the first position.
 */
export const moveToFirstPosition = (array: any[], value: any, field: string) => {
  if (!Array.isArray(array) || !value || !field) {
    return array
  }
  const index = array.findIndex((obj) => obj[field] === value)

  if (index !== -1) {
    const [foundObject] = array.splice(index, 1)
    array.unshift(foundObject)
  }
  return array
}

/**
 * Decodes a `uniqueKey` string into an object containing the `registrationId`, `programId`, and `materialLPID` values.
 * The `uniqueKey` is expected to be a string with three parts separated by the pipe (`|`) character.
 *
 * @param uniqueKey - An optional string in the format `registrationId|programId|materialLPID`.
 *
 * @returns An object with the properties `registrationId`, `programId`, and `materialLPID` if the `uniqueKey` is valid.
 *          If no `uniqueKey` is provided or it is empty, an empty object is returned.
 * @example
 * // Example 1: Valid `uniqueKey`
 * const result = decodeDDUniqueKey('1234|5678|abcd');
 * console.log(result);
 * // Output: { registrationId: '1234', programId: '5678', materialLPID: 'abcd' }
 *
 * // Example 2: No `uniqueKey` provided
 * const resultEmpty = decodeDDUniqueKey();
 * console.log(resultEmpty);
 * // Output: {}
 *
 * // Example 3: Invalid `uniqueKey` format (missing parts)
 * const resultInvalid = decodeDDUniqueKey('1234|5678');
 * console.log(resultInvalid);
 * // Output: { registrationId: '1234', programId: '5678', materialLPID: undefined }
 */
export const decodeDDUniqueKey = (uniqueKey?: string) => {
  if (!uniqueKey) return {}
  const [registrationId, programId, materialLPID] = uniqueKey.split('|')
  return { registrationId, programId, materialLPID }
}

export function getLanguageCode(language: string): string {
  switch (language.toLowerCase()) {
    case 'german':
      return 'de'
    case 'spanish':
      return 'es'
    case 'french':
      return 'fr'
    default:
      return 'en'
  }
}
